import React, { useState, useRef, useEffect } from "react";
import { View, Pressable } from "react-native-web";
import { borderRadius, typography, shadows } from "../../utils/styling";
import Button from "../common/Button";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import CustomText from "../atoms/CustomText";
import { colors, spacing } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import GeneralDialog from '../../components/common/GeneralDialog';
import { downloadPDF, generatePDFBlob } from "../../lib/pdf-utils";
import { uploadPDFToSupabaseSimple } from "../../lib/supabase-pdf";
import { sendPdfEmail, trackPDFDownload } from "../../lib/stripe";
import { CATEGORY_SECTIONS, SPECIFIC_CATEGORY_SECTIONS } from "../../utils/category_mappings";
import { clearMoneyPersonalityAssessmentState } from "../../utils/quizRetake";


export default function MainHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();

  const [atTop, setAtTop] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isManualDownloading, setIsManualDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [transactionData, setTransactionData] = useState<{
    status: string;
    transaction_id: string | null;
    error?: string;
  } | null>(null);
  const [dialogType, setDialogType] = useState<'download' | 'info-collection' | 'main-results'>('download');

  // Two audiences, one header (Pacific Life pattern, McKayla 2026-09-23): the site defaults to the
  // individual front door; advisor marketing pages show advisor nav + a way back.
  const ADVISOR_PATHS = ["/advisors", "/pricing", "/demo", "/who-we-serve", "/resources"];
  const isAdvisorView = ADVISOR_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  );
  const navLinks = isAdvisorView
    ? [
        { label: "Personalities", path: "/personalities" },
        { label: "Pricing", path: "/pricing" },
        { label: "Resources", path: "/resources" },
      ]
    : [
        { label: "Personalities", path: "/personalities" },
        { label: "About", path: "/about" },
        { label: "FAQ", path: "/faq" },
      ];

  const handleQuizNavigation = () => {
    console.log("HANDLE")
    // Check if they already took quiz 
    const stored = localStorage.getItem('mpay_resultsData');
    if (stored) {
      // If taken quiz, check if they went through info collection to download PDF
      // TODO this is for paying logic
      // const hasPaid = (transactionData?.status === 'found' && transactionData.transaction_id) || localStorage.getItem('payment_success') !== null || localStorage.getItem('mpay_coupon_redeemed') !== null;
      // const personalInfo = localStorage.getItem('mpay_personalInfo');
      // const hasCompletedInfoCollection = personalInfo !== null;
      // const showDownload = hasPaid && hasCompletedInfoCollection;

      // TODO this is for free month
      const hasPaid = true;
      const personalInfo = localStorage.getItem('mpay_personalInfo');
      const hasCompletedInfoCollection = personalInfo !== null;
      const showDownload = hasPaid && hasCompletedInfoCollection;
      // If they already went through the info collection process, then give them option to 
      // download their PDF + send it to them
      if (showDownload) {
        setDialogType('download');
        setShowDialog(true);
      } else if (hasPaid) {  // they have taken quiz (and paid) but not info collection, so suggest them to complete it + navigate
        setDialogType('info-collection');
        setShowDialog(true);
      } else { // they have taken the quiz only, without paying, so go back to main results
        setDialogType('main-results');
        setShowDialog(true);
      }
    } else {  // haven't taken quiz, so navigate there
      navigate('/quiz', { state: { from: location.pathname }} );
    }
  };

  const handleEmailPDF = async () => {
    setIsEmailing(true);
    
    try {
      // Get results data from localStorage
      const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
      const personalInfo = JSON.parse(localStorage.getItem('mpay_personalInfo') || '{}');
      
      if (!resultsData.email || !resultsData.quizId) {
        console.error('Missing email or quizId in results data');
        return;
      }

      // Get transaction ID
      let transactionId = "Client";
      if (transactionData?.status === 'found' && transactionData.transaction_id) {
        transactionId = transactionData.transaction_id;
      }

      // Generate PDF
      console.log('📄 Generating PDF...');
      const pdfBlob = await generatePDFBlob({
        answers: resultsData.answers || [],
        categoryScores: resultsData.categoryScores || {},
        dominantCategory: resultsData.dominantCategory || "",
        personalInfo: personalInfo
      });

      // Upload to Supabase
      const emailHash = resultsData.email.replace(/[^a-zA-Z0-9]/g, '');
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `personality-profile-${emailHash}-${dateStr}`;
      
      const uploadResult = await uploadPDFToSupabaseSimple(pdfBlob, filename);
      
      // Track PDF download
      if (resultsData.quizId && resultsData.email) {
        try {
          await trackPDFDownload(resultsData.quizId, resultsData.email, filename, uploadResult.url);
        } catch (trackError) {
          console.warn('⚠️ PDF tracking failed (non-critical):', trackError);
        }
      }

      // Send email
      console.log('📧 Sending email...');
      await sendPdfEmail(
        resultsData.email,
        resultsData.quizId,
        resultsData,
        transactionId,
        CATEGORY_SECTIONS,
        SPECIFIC_CATEGORY_SECTIONS,
        resultsData.dominantCategory,
        resultsData.specificDominantCategory,
        uploadResult.url
      );

      console.log('✅ PDF emailed successfully');
    } catch (error) {
      console.error('❌ Failed to email PDF:', error);
    } finally {
      setIsEmailing(false);
    }
  }

  const handleDownloadPDF = async () => {
    setIsManualDownloading(true);
    
    try {
      // Get results data from localStorage  
      const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
      const personalInfo = JSON.parse(localStorage.getItem('mpay_personalInfo') || '{}');
      
      const filename = `financial-personality-profile-${new Date().toISOString().split('T')[0]}`;
      
      // Download PDF directly
      await downloadPDF({
        answers: resultsData.answers || [],
        categoryScores: resultsData.categoryScores || {},
        dominantCategory: resultsData.dominantCategory || "",
        personalInfo: personalInfo
      }, filename);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('❌ Failed to download PDF:', error);
    } finally {
      setIsManualDownloading(false);
    }
  }

  const handleResultsNavigation = () => {
    // Navigate to info collection
    const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
    navigate('/quiz/results-ready', { state: resultsData });
  }

  const handlePortalNavigation = () => {
    const resultsData = JSON.parse(localStorage.getItem('mpay_resultsData') || '{}');
    navigate('/login', { state: resultsData })
  }

  const handleRetakeQuiz = () => {
    clearMoneyPersonalityAssessmentState();
    setShowDialog(false);
    navigate('/quiz', { state: { from: location.pathname, retake: true } });
  };

  const renderRetakeButton = () => (
    <Pressable
      onPress={handleRetakeQuiz}
      style={({ hovered }: { hovered: boolean }) => ({
        backgroundColor: hovered ? '#f5f5f5' : 'white',
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.lg,
        border: '1px solid black',
        height: isMobile ? 40 : 50,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      })}
    >
      <CustomText style={{
        color: 'black',
        fontSize: 16,
        fontWeight: '600',
        fontFamily: 'Nunito',
      }}>
        Retake quiz
      </CustomText>
    </Pressable>
  );

  useEffect(() => {
    const onScroll = (e: Event) => {
      const yPos = window.scrollY;
      if (yPos < 10) {
        setAtTop(true);
      } else {
        setAtTop(false);
      }
    };
    
    window.addEventListener("scroll", onScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (window.transactionData) {
      // @ts-ignore
      setTransactionData(window.transactionData);
    }
    
    // Listen for changes to global transaction data
    const checkTransactionData = () => {
      // @ts-ignore
      if (window.transactionData && window.transactionData !== transactionData) {
        // @ts-ignore
        setTransactionData(window.transactionData);
      }
    };
    
    const interval = setInterval(checkTransactionData, 1000);
    return () => clearInterval(interval);
  }, [transactionData]);

  return (
    <>
      <View
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: 0,
          zIndex: 10,
          border: "none",
          borderBottom: `1px solid rgba(255, 255, 255, 0.2)`,
          borderLeft: `1px solid rgba(255, 255, 255, 0.2)`,
          borderRight: `1px solid rgba(255, 255, 255, 0.2)`,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <View
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingLeft: isMobile ? spacing.lg : spacing.xl,
            paddingRight: isMobile ? spacing.lg : spacing.xl,
            paddingTop: spacing.lg,
            paddingBottom: spacing.lg,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Left: Ghost Placeholder + Logo */}
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              overflow: "hidden",
              height: isMobile ? 50 : 60,
              width: 195,
            }}
          />
          <View
            style={{
              position: 'absolute',
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={require("../img/money_personality_full_logo.png")}
              alt="FinAI Logo"
              style={{
                height: isMobile ? 50 : 60,
                width: "auto",
                objectFit: "contain",
                display: "block",
                cursor: "pointer",
                marginRight: "20px",
              }}
              onClick={() => navigate(isAdvisorView ? "/advisors" : "/")}
            />
          </View>

          {/* Center: Navigation Tabs - Only show on desktop */}
          {!isMobile && (
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: spacing.xxl,
              }}
            >
              {navLinks.map((link) => (
                <Pressable
                  key={link.label}
                  style={({
                    hovered,
                    pressed,
                  }: {
                    hovered: boolean;
                    pressed: boolean;
                  }) => ({
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: 15,
                    paddingVertical: 10,
                    paddingHorizontal: 20,
                    backgroundColor:
                      hovered || pressed
                        ? "rgba(255, 255, 255, 0.5)"
                        : "rgba(255, 255, 255, 0.3)",
                    transition: "background-color 0.2s ease-in",
                  })}
                  onPress={() => navigate(link.path)}
                >
                  <CustomText
                    style={{
                      color: colors.dark,
                      fontWeight: "500",
                      fontFamily: typography.fontFamily.body,
                    }}
                  >
                    {link.label}
                  </CustomText>
                </Pressable>
              ))}
            </View>
          )}

          {/* Right: Login and Take Quiz */}
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: spacing.lg,
            }}
          >
            {!isMobile && (
              <Pressable
                style={({ hovered }: { hovered: boolean }) => ({
                  paddingVertical: "10px",
                  paddingHorizontal: "12px",
                  cursor: "pointer",
                  opacity: hovered ? 0.7 : 1,
                })}
                onPress={() => navigate(isAdvisorView ? "/" : "/advisors")}
              >
                <CustomText
                  style={{
                    fontFamily: typography.fontFamily.body,
                    fontSize: 16,
                    color: "#485A6B",
                    fontWeight: "600",
                    textDecorationLine: "underline",
                  }}
                >
                  {isAdvisorView ? "For Individuals" : "For Advisors"}
                </CustomText>
              </Pressable>
            )}
            {!isMobile && isAdvisorView && (
              <Pressable
                style={({
                  hovered,
                  pressed,
                }: {
                  hovered: boolean;
                  pressed: boolean;
                }) => ({
                  paddingHorizontal: "20px",
                  maxHeight: "40px",
                  borderRadius: borderRadius.md,
                  backgroundColor: hovered ? "#f5f5f5" : "#ffffff",
                  border: "1px solid #485A6B",
                  transition: "background-color 0.2s ease-in",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  paddingVertical: "10px",
                })}
                onPress={() => navigate("/login")}
              >
                <CustomText
                  style={{
                    fontFamily: typography.fontFamily.body,
                    fontSize: 16,
                    color: "#485A6B",
                    fontWeight: "600",
                  }}
                >
                  Sign in
                </CustomText>
              </Pressable>
            )}
            {!isMobile && (
              <Button
                variant="primary"
                label={isAdvisorView ? "Book a demo" : "Take the quiz"}
                style={{
                  paddingHorizontal: "20px",
                  maxHeight: "40px",
                  borderRadius: borderRadius.md,
                  backgroundColor: "#485A6B",
                }}
                textStyle={{
                  fontFamily: typography.fontFamily.body,
                  fontSize: 16,
                  fontWeight: "700",
                }}
                onPress={() => (isAdvisorView ? navigate("/demo") : handleQuizNavigation())}
              />
            )}
            {isMobile && (
              <Pressable
                onPress={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{ padding: spacing.sm }}
              >
                <View
                  style={{
                    width: 24,
                    height: 2,
                    backgroundColor: colors.dark,
                    marginBottom: 6,
                  }}
                />
                <View
                  style={{
                    width: 24,
                    height: 2,
                    backgroundColor: colors.dark,
                    marginBottom: 6,
                  }}
                />
                <View
                  style={{ width: 24, height: 2, backgroundColor: colors.dark }}
                />
              </Pressable>
            )}
          </View>
        </View>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <View
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: colors.white,
              borderBottomLeftRadius: borderRadius.lg,
              borderBottomRightRadius: borderRadius.lg,
              boxShadow: shadows.lg,
              padding: spacing.lg,
            }}
          >
            {[
              isAdvisorView
                ? { label: "Book a demo", onPress: () => navigate("/demo"), bold: true }
                : { label: "Take the quiz", onPress: () => handleQuizNavigation(), bold: true },
              ...navLinks.map((link) => ({ label: link.label, onPress: () => navigate(link.path), bold: false })),
              ...(isAdvisorView ? [{ label: "Sign in", onPress: () => navigate("/login"), bold: false }] : []),
              {
                label: isAdvisorView ? "For Individuals" : "For Advisors",
                onPress: () => navigate(isAdvisorView ? "/" : "/advisors"),
                bold: false,
              },
            ].map((entry) => ({
              label: entry.label,
              onPress: () => {
                entry.onPress();
                setMobileMenuOpen(false);
              },
              style: entry.bold
                ? { color: colors.dark, fontSize: 18, fontWeight: "700" }
                : { color: colors.dark, fontSize: 16 },
            })).map((item) => (
              <Pressable
                key={item.label}
                style={({
                  hovered,
                  pressed,
                }: {
                  hovered: boolean;
                  pressed: boolean;
                }) => ({
                  padding: spacing.md,
                  backgroundColor:
                    hovered || pressed ? colors.gray[200] : "transparent",
                  borderRadius: 8,
                  transition: "background 0.15s",
                  cursor: "pointer",
                })}
                onPress={item.onPress}
              >
                <CustomText style={item.style}>{item.label}</CustomText>
              </Pressable>
            ))}
          </View>
        )}
      </View>
      {showDialog && dialogType === 'download' && (
        <GeneralDialog
          title="Wait... You look familiar!"
          description="It appears you have already taken the Money Personality Assessment. You can download your personalized PDF report or have it emailed to you. You can also login to the portal."
          yesText="Close"
          noText=""
          onConfirm={() => setShowDialog(false)}
          onClose={() => setShowDialog(false)}
        >
          <View style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.md,
            marginTop: spacing.md,
          }}>
            <Pressable
              onPress={handleDownloadPDF}
              disabled={isManualDownloading}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isManualDownloading ? colors.gray[400] : (hovered ? '#515A64' : 'black'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: isMobile ? 40 : 50,
                cursor: isManualDownloading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isManualDownloading ? 0.7 : 1,
              })}
            >
              <CustomText style={{
                color: colors.white,
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                {isManualDownloading ? 'Generating PDF...' : 'Download PDF to my device'}
              </CustomText>
            </Pressable>
            
            <Pressable
              onPress={handleEmailPDF}
              disabled={isEmailing}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isEmailing ? colors.gray[400] : (hovered ? '#f5f5f5' : 'white'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                border: '1px solid black',
                height: isMobile ? 40 : 50,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEmailing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isEmailing ? 0.7 : 1,
              })}
            >
              <CustomText style={{
                color: 'black',
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                {isEmailing ? 'Emailing PDF...' : 'Email me my PDF'}
              </CustomText>
            </Pressable>
            <Pressable
              onPress={handlePortalNavigation}
              disabled={isEmailing}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: isEmailing ? colors.gray[400] : (hovered ? '#f5f5f5' : 'white'),
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.lg,
                border: '1px solid black',
                height: isMobile ? 40 : 50,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isEmailing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isEmailing ? 0.7 : 1,
              })}
            >
              <CustomText style={{
                color: 'black',
                fontSize: 16,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                Portal Login
              </CustomText>
            </Pressable>
            {renderRetakeButton()}
          </View>
        </GeneralDialog>
      )}

      {showDialog && dialogType === 'info-collection' && (
        <GeneralDialog
          title="Final Steps"
          description="You have already taken the Money Personality assessment. To unlock your personalized PDF report, please complete a quick information collection form in your portal. This will only take about 1 minute."
          yesText="Take me there"
          noText="Cancel"
          onConfirm={handlePortalNavigation}
          onClose={() => setShowDialog(false)}
        >
          <View style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <CustomText style={{
              fontSize: 14,
              color: colors.gray[600],
              fontFamily: 'Nunito',
              textAlign: 'center',
            }}>
              Your results are ready and waiting for you!
            </CustomText>
            <View style={{
              width: '100%',
              marginTop: spacing.md,
            }}>
              {renderRetakeButton()}
            </View>
          </View>
        </GeneralDialog>
      )}

      {showDialog && dialogType === 'main-results' && (
        <GeneralDialog
          title="Gain Full Access"
          description="It appears you have taken the Money Personality Quiz already. To view your initial results, please click the button below."
          yesText="Take me to my results"
          noText="Cancel"
          onConfirm={handleResultsNavigation}
          onClose={() => setShowDialog(false)}
        >
          <View style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: spacing.md,
            marginBottom: spacing.sm,
          }}>
            <CustomText style={{
              fontSize: 14,
              color: colors.gray[600],
              fontFamily: 'Nunito',
              textAlign: 'center',
            }}>
              Your results are ready and waiting for you!
            </CustomText>
            <View style={{
              width: '100%',
              marginTop: spacing.md,
            }}>
              {renderRetakeButton()}
            </View>
          </View>
        </GeneralDialog>
      )}
      <Outlet />
    </>
  );
}
